import {
  WHEEL_ZOOM_PER_NOTCH,
  PINCH_WHEEL_DELTA_LIMIT,
  PINCH_WHEEL_ZOOM_PER_DELTA,
  WHEEL_LINE_HEIGHT_PX,
  KEYBOARD_PAN_BARS,
} from "../constants";
import { isInPaneArea } from "./hitRegions";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// WheelEvent.deltaMode: 0 pixels, 1 lines, 2 pages
const DELTA_MODE_LINE = 1;
const DELTA_MODE_PAGE = 2;

const toPixels = (delta, deltaMode, pageSize) => {
  if (deltaMode === DELTA_MODE_LINE) return delta * WHEEL_LINE_HEIGHT_PX;
  if (deltaMode === DELTA_MODE_PAGE) return delta * pageSize;

  return delta;
};

// Positive zooms in; exp keeps zoom in and zoom out symmetric
const zoomBy = (timeScale, x, amount) => {
  timeScale.zoomAt(x, timeScale.barSpacing * Math.exp(amount));
};

/**
 * Wheel over the chart. Only gestures meant for the chart are taken, so the
 * page keeps scrolling under a plain wheel (phones, laptops):
 * - Ctrl + wheel (also a trackpad pinch) zooms time with the bar under the
 *   cursor in place; over the price axis around the right edge of the pane,
 *   since prices always autoscale and the axis is a zoom zone for time;
 * - horizontal scroll (trackpad, Shift + wheel) pans time;
 * - vertical wheel scrolls the oscillator panes when they do not all fit
 *   and the pointer is over them (D24); otherwise it is left to the page.
 * Only the dominant direction is used, so a slightly diagonal swipe does not zoom while panning.
 *
 * @param {WheelEvent} event
 * @param {{ x: number, y: number }} point - CSS px of the chart
 * @param {object} view - see createInteraction
 * @returns {boolean} true when the chart took the wheel (the page must not scroll)
 */
export const handleWheel = (event, point, view) => {
  const { timeScale, priceScale, paneStack } = view;
  let deltaX = toPixels(event.deltaX, event.deltaMode, timeScale.width);
  let deltaY = toPixels(event.deltaY, event.deltaMode, priceScale.height);

  if (event.shiftKey && deltaX === 0) {
    deltaX = deltaY;
    deltaY = 0;
  }

  const isVertical = deltaY !== 0 && Math.abs(deltaY) >= Math.abs(deltaX);

  if (isVertical && event.ctrlKey) {
    const amount = clamp(deltaY, -PINCH_WHEEL_DELTA_LIMIT, PINCH_WHEEL_DELTA_LIMIT) * PINCH_WHEEL_ZOOM_PER_DELTA;

    zoomBy(timeScale, clamp(point.x, 0, timeScale.width), -amount);
  } else if (isVertical) {
    if (!paneStack.canScroll || !isInPaneArea(point, view.overlay.layout)) return false;

    // Scrolling the panes schedules the frame itself
    paneStack.scrollBy(deltaY);
    return true;
  } else if (deltaX !== 0) {
    timeScale.scrollBy(-deltaX);
  } else {
    return false;
  }

  view.invalidate();

  return true;
};

/**
 * Keys of the focused chart: ← → pan by a bar; ↑ ↓ and Ctrl + → ← zoom with
 * the right edge in place, like one wheel notch.
 *
 * @param {KeyboardEvent} event
 * @param {object} view - see createInteraction
 * @returns {boolean} true when the key was handled
 */
export const handleKeyDown = (event, view) => {
  const { timeScale } = view;
  const zoom = (direction) => zoomBy(timeScale, timeScale.width, direction * WHEEL_ZOOM_PER_NOTCH);
  const pan = (direction) => timeScale.scrollBy(direction * KEYBOARD_PAN_BARS * timeScale.barSpacing);
  const actions = {
    ArrowLeft: () => (event.ctrlKey ? zoom(-1) : pan(1)),
    ArrowRight: () => (event.ctrlKey ? zoom(1) : pan(-1)),
    ArrowUp: () => zoom(1),
    ArrowDown: () => zoom(-1),
  };
  const action = actions[event.key];

  if (!action || event.altKey || event.metaKey) return false;

  action();
  view.invalidate();

  return true;
};
